import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TemplateProvider } from '@/contexts/TemplateContext';
import { DateSelection } from './DateSelection';
import { TimeSlotSelection } from './TimeSlotSelection';
import { ServiceSelection } from './ServiceSelection';
import { CustomerDetailsForm } from './CustomerDetailsForm';
import { BookingConfirmation } from './BookingConfirmation';
import { PhoneVerification } from './PhoneVerification';
import { LocationPicker, type BookingLocationOption } from './LocationPicker';
import { MaterialsSelection } from './MaterialsSelection';
import { useBookingWizard } from '@/hooks/useBookingWizard';
import { formatCurrencyAmount } from '@/utils/currency';
import { stepLabel, type BookingEntryMode } from './bookingWizardSteps';
import type { Service, ServiceCategory } from '@/types';

interface BookingWizardProps {
  profileId: string;
  username: string;
  profileTimezone?: string;
  services: Service[];
  categories: ServiceCategory[];
  colorScheme?: string;
  typography?: string;
  defaultCurrency?: string;
  supportedCurrencies?: string[];
  verificationMethod?: 'email' | 'phone';
  clientVerificationEnabled?: boolean;
  entryMode?: BookingEntryMode;
  fixedStaffId?: string | null;
  locationOptions?: BookingLocationOption[];
  variant?: 'page' | 'modal' | 'embed';
  allowCustomerStaffSelection?: boolean;
  onComplete: () => void;
  onCancel: () => void;
}

export const BookingWizard: React.FC<BookingWizardProps> = ({
  profileId,
  username,
  profileTimezone,
  services,
  categories,
  colorScheme = 'default',
  typography = 'default',
  defaultCurrency = 'EUR',
  supportedCurrencies = [],
  verificationMethod = 'email',
  clientVerificationEnabled = true,
  entryMode = 'location',
  fixedStaffId = null,
  locationOptions = [],
  variant = 'page',
  allowCustomerStaffSelection = true,
  onComplete,
  onCancel,
}) => {
  const { t } = useTranslation('booking');
  const [phoneVerified, setPhoneVerified] = useState(false);

  const wizard = useBookingWizard({
    entryMode,
    profileId,
    username,
    initialServices: [],
    defaultCurrency,
    supportedCurrencies,
    verificationMethod,
    clientVerificationEnabled,
    fixedStaffId,
    locationOptions,
    variant,
    allowCustomerStaffSelection,
  });

  const {
    currentStep,
    stepOrder,
    selectedServices,
    selectedDate,
    selectedSlot,
    customerInfo,
    bookingResult,
    imagePaths,
    sessionId,
    profileId: activeProfileId,
    bookableMaterials,
    materialSelections,
    setMaterialSelections,
    capableStaff,
    availableSlots,
    isLoading,
    error,
    isPolling,
    pollingCount,
    selectLocation,
    selectServices,
    selectAssignedUser,
    selectDate,
    selectSlot,
    selectCurrency,
    updateCustomerInfo,
    addImagePath,
    removeImagePath,
    fetchAvailableSlots,
    goNext,
    goBack,
    canProceed,
    totalDuration,
    totalPrice,
    selectedCurrency,
    supportedCurrencies: wizardCurrencies,
    requiresEmail,
    clientVerificationEnabled: verifyEnabled,
    reset,
    pollConfirmationStatus,
    assignedUserId,
  } = wizard;

  const showMultipleLocations = services.some((s) => s.profile?.business_name);
  const isEmbed = variant === 'embed';
  const isModal = variant === 'modal';
  const shellClass = isEmbed || isModal ? 'bg-transparent' : 'min-h-screen bg-gray-50';

  const handleContinue = async () => {
    await goNext();
  };

  const handlePhoneVerified = async (status: string) => {
    setPhoneVerified(true);
    if (status === 'confirmed' || status === 'pending_confirmation') return;
    await pollConfirmationStatus();
  };

  const handleBookAnother = () => {
    setPhoneVerified(false);
    reset();
  };

  const progressSteps = stepOrder.filter((s) => s !== 'done');

  const wizardContent = (
    <div className={shellClass}>
      <div className={`${isEmbed || isModal ? '' : 'bg-white shadow-sm border-b'}`}>
        <div className={`${isEmbed || isModal ? '' : 'max-w-3xl mx-auto px-4 py-4'}`}>
          <div className="flex items-center justify-between overflow-x-auto gap-2">
            {progressSteps.map((s, idx) => (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center min-w-[4rem]">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                      stepOrder.indexOf(currentStep) >= stepOrder.indexOf(s)
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {stepOrder.indexOf(currentStep) > stepOrder.indexOf(s) ? '✓' : idx + 1}
                  </div>
                  <span className="text-[10px] mt-1 text-center text-gray-500">
                    {t(`steps.${s}`, { defaultValue: stepLabel(s) })}
                  </span>
                </div>
                {idx < progressSteps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 min-w-[1rem] ${
                      stepOrder.indexOf(currentStep) > stepOrder.indexOf(s)
                        ? 'bg-indigo-600'
                        : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className={`${isEmbed || isModal ? '' : 'max-w-3xl mx-auto px-4 py-8'}`}>
        <div className={`${isEmbed || isModal ? '' : 'bg-white rounded-xl shadow-sm border p-6'}`}>
          {currentStep === 'location' && (
            <LocationPicker
              locations={locationOptions}
              value={activeProfileId}
              onChange={selectLocation}
            />
          )}

          {currentStep === 'services' && (
            <div className="space-y-6">
              {wizardCurrencies.length > 1 && (
                <div className="flex items-center justify-end gap-2">
                  <label htmlFor="currency" className="text-sm text-gray-600">
                    {t('service.currencyLabel')}
                  </label>
                  <select
                    id="currency"
                    value={selectedCurrency}
                    onChange={(e) => selectCurrency(e.target.value)}
                    className="rounded-lg border px-3 py-2 text-sm"
                  >
                    {wizardCurrencies.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <ServiceSelection
                services={services.filter(
                  (s) => !activeProfileId || s.profile_id === activeProfileId || !s.profile_id
                )}
                categories={categories}
                selectedServices={selectedServices}
                onServicesChange={selectServices}
                totalDuration={totalDuration}
                totalPrice={totalPrice}
                currency={selectedCurrency}
                showLocationLabels={showMultipleLocations}
              />
            </div>
          )}

          {currentStep === 'materials' && (
            <MaterialsSelection
              materials={bookableMaterials}
              selections={materialSelections}
              currency={selectedCurrency}
              onChange={setMaterialSelections}
            />
          )}

          {currentStep === 'staff' && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-center">
                {t('staff.prefer', { defaultValue: 'Choose your provider' })}
              </h2>
              <select
                className="w-full rounded-md border px-3 py-2"
                value={assignedUserId ?? ''}
                onChange={(e) => selectAssignedUser(e.target.value || null)}
              >
                <option value="">
                  {t('staff.noPreference', { defaultValue: 'No preference (auto-assign)' })}
                </option>
                {capableStaff.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.full_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {currentStep === 'date' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{t('datetime.selectDate')}</h2>
              </div>
              <DateSelection selectedDate={selectedDate} onDateChange={selectDate} />
            </div>
          )}

          {currentStep === 'time' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{t('datetime.selectTime')}</h2>
              </div>
              <TimeSlotSelection
                selectedSlot={selectedSlot}
                availableSlots={availableSlots}
                isLoading={isLoading}
                onSlotChange={selectSlot}
                onSlotsRefresh={fetchAvailableSlots}
                profileId={activeProfileId}
                date={selectedDate}
                serviceIds={selectedServices.map((s) => s.id)}
              />
            </div>
          )}

          {currentStep === 'details' && (
            <CustomerDetailsForm
              customerInfo={customerInfo}
              onChange={updateCustomerInfo}
              requiresPhone
              requiresEmail={requiresEmail}
              clientVerificationEnabled={verifyEnabled}
              imagePaths={imagePaths}
              onImageUploaded={addImagePath}
              onImageRemoved={removeImagePath}
              uploadContext={{
                profileId: activeProfileId,
                date: selectedDate || '',
                time: selectedSlot || '',
                sessionId,
                assignedUserId,
              }}
            />
          )}

          {currentStep === 'done' &&
            verifyEnabled &&
            bookingResult?.verification_method === 'phone' &&
            !phoneVerified &&
            bookingResult.status === 'pending_verification' && (
              <PhoneVerification
                bookingId={bookingResult.id}
                phone={bookingResult.customer_phone || customerInfo.phone}
                onVerified={handlePhoneVerified}
                onBackToProfile={onComplete}
              />
            )}

          {currentStep === 'done' &&
            !(
              verifyEnabled &&
              bookingResult?.verification_method === 'phone' &&
              !phoneVerified &&
              bookingResult.status === 'pending_verification'
            ) && (
              <BookingConfirmation
                booking={bookingResult}
                profileName={username}
                profileTimezone={profileTimezone}
                isPolling={isPolling}
                pollingCount={pollingCount}
                currency={selectedCurrency}
                onBookAnother={handleBookAnother}
                onViewDetails={onComplete}
              />
            )}
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {currentStep !== 'done' && (
          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={
                currentStep === stepOrder[0] ? onCancel : goBack
              }
              className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium"
            >
              {currentStep === stepOrder[0] ? t('buttons.cancel') : t('buttons.back')}
            </button>
            <div className="flex items-center gap-4">
              {totalPrice > 0 && currentStep !== 'services' && (
                <span className="text-sm font-semibold text-gray-700">
                  {formatCurrencyAmount(totalPrice, selectedCurrency)}
                </span>
              )}
              <button
                type="button"
                onClick={handleContinue}
                disabled={!canProceed || isLoading}
                className="px-8 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isLoading
                  ? t('buttons.loading')
                  : currentStep === 'details'
                    ? t('buttons.submit')
                    : t('buttons.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <TemplateProvider colorScheme={colorScheme} typography={typography}>
      {wizardContent}
    </TemplateProvider>
  );
};
